import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { saveReadingPosition } from './readingPosition';
import findReadingIndex from './findReadingIndex';

export default function useReadingPosition({ containerRef, ready, content, section, type, bookName }) {
  const { hash } = useLocation();
  useEffect(() => {
    if (!ready || !containerRef.current) return;
    const container = containerRef.current;
    // English editions use table rows instead of the Japanese verse markers.
    const markers = Array.from(container.querySelectorAll('em, tr')).map((element) => {
      const match = element.tagName === 'TR'
        ? element.querySelector('td.vn')?.textContent.trim().match(/^(\d+):(\d+)$/)
        : element.id.match(/^(\d+)-(\d+)$/) || element.textContent.trim().match(/^(\d+):(\d+)$/);
      return match ? { element, chapter: Number(match[1]), verse: Number(match[2]) } : null;
    }).filter(Boolean);
    if (!markers.length) return;

    const groups = [];
    markers.forEach((marker) => {
      const element = marker.element.closest('.bible-reading-chapter') || container;
      if (groups[groups.length - 1]?.element !== element) groups.push({ element, markers: [] });
      groups[groups.length - 1].markers.push(marker);
    });

    let lastSaved = '';
    let timer;
    const save = (marker) => {
      const key = `${marker.chapter}-${marker.verse}`;
      if (lastSaved === key) return;
      lastSaved = key;
      saveReadingPosition({ section, type, bookName, chapter: marker.chapter, verse: marker.verse });
    };
    const capture = () => {
      // Read only chapter boxes, then markers inside the selected chapter.
      // Measuring markers in distant skipped chapters would force rendering.
      const groupIndex = Math.max(0, findReadingIndex(groups));
      const candidates = groups[groupIndex].markers;
      const index = findReadingIndex(candidates);
      const previous = groups[groupIndex - 1]?.markers;
      save(index >= 0 ? candidates[index] : previous ? previous[previous.length - 1] : markers[0]);
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(capture, 180);
    };
    const frame = requestAnimationFrame(() => {
      const resume = hash.match(/^#resume=(\d+)-(\d+)$/);
      const target = resume && markers.find((marker) => marker.chapter === Number(resume[1]) && marker.verse === Number(resume[2]));
      if (target) {
        window.scrollTo({ top: Math.max(0, target.element.getBoundingClientRect().top + window.scrollY - 150), behavior: 'instant' });
        save(target);
      } else {
        capture();
      }
    });
    const onHide = () => {
      if (document.visibilityState === 'hidden') capture();
    };
    const onNavigate = (event) => {
      if (event.target.closest?.('a[href]')) capture();
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('pagehide', capture);
    document.addEventListener('visibilitychange', onHide);
    document.addEventListener('click', onNavigate, true);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('pagehide', capture);
      document.removeEventListener('visibilitychange', onHide);
      document.removeEventListener('click', onNavigate, true);
    };
  }, [containerRef, ready, content, section, type, bookName, hash]);
}
